'use client'

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { pathToUrl } from '../../../utils/router'
import routes from '../../../routes'
import { BuilderContext } from './utils'
import {
  checkPublishStatus,
  publishPreviewWidget,
  publishWidget,
  resetPublishStatus,
  saveWidget,
  useCheckIsInABTest,
} from '../../../api/widgets'
import Header from '../../../components/Header/Header'
import Flex from '../../../components/UI/Flex'
import BuilderSideMenu from './BuilderSideMenu'
import Box from '../../../components/UI/Box'
import Footer from '../../../components/Footer/Footer'
import styled from 'styled-components'
import Grid from '../../../components/UI/Grid'
import WidgetPreview from '../../../components/WidgetPreview/WidgetPreview'
import IconButton from '../../../components/UI/IconButton'
import {
  AiOutlineCheck,
  AiOutlineCloudUpload,
  AiOutlineEye,
  AiOutlineQuestionCircle,
} from 'react-icons/ai'
import ShareCode from '../../../components/ShareCode/ShareCode'
import { BsCodeSlash } from 'react-icons/bs'
import { Color } from '../../../interfaces/designSystem'
import { useNotification } from '@ui/NotificationProvider'
import { withHandledErrors } from '@utils/errors'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import MainOnboarding from '@components/Onboarding/MainOnboarding'
import AfterPublishPopup from '@components/AfterPublishPopup/AfterPublishPopup'
import { useIsDemo } from '@utils/hooks'
import { useGetProfile } from '@api/users'
import { Tooltip } from 'antd'
import AiWizard from '@components/AIWizard/AIWizard'
import HelpPopup from './HelpPopup'
import { AnalyticsEvents, sendAnalyticsEvent } from '@utils/analytics'

const Wrap = styled(Box)`
  flex: 1 0 auto;
`

const MainContent = styled(Box)`
  width: 330px;
  flex-shrink: 0;

  @media (max-width: 980px) {
    flex-grow: 1;
  }
`

const HeaderBtnContent = styled.span`
  @media (max-width: 850px) {
    display: none;
  }
`

const PreviewBtn = styled(IconButton)`
  display: none;
  margin: 8px 16px;

  @media (max-width: 980px) {
    display: block;
  }
`

type Props = { children: React.ReactNode }

const BuilderPage = ({ children }: Props) => {
  const router = useRouter()
  const currentRoute = usePathname()
  const notification = useNotification()
  const searchParams = useSearchParams()
  const profile = useGetProfile()
  const { widget, setWidget } = useContext(BuilderContext)
  const [shareCodeOpen, setShareCodeOpen] = useState(false)
  const [savedWidget, setSavedWidget] = useState(widget)
  const [showPreview, setShowPreview] = useState(false)
  const [state, setState] = useState<
    'previewLoading' | 'publishLoading' | 'publishQueuing' | 'saveLoading'
  >()
  const isDemo = useIsDemo()
  const [showOnboarding, setShowOnboarding] = useState(isDemo)
  const [showAfterPublish, setShowAfterPublish] = useState(false)
  const timerId = useRef<NodeJS.Timer>()
  const { data: isInABTest } = useCheckIsInABTest(widget.id!)
  const [openWizard, setOpenWizard] = useState(false)
  const isNew = !!searchParams?.get('new')
  const [openHelp, setOpenHelp] = useState(false)

  const isAnalytics =
    currentRoute === pathToUrl(routes.privateRoutes.builder.analytics, { id: widget.id })

  const changed = savedWidget !== widget

  const fetchStatus = useCallback(async () => {
    clearTimeout(timerId.current)
    const resp = await checkPublishStatus(widget.id!, false)

    if (resp.status === 'success') {
      setState(undefined)

      if (profile?.data && !profile.data.doNotShowAfterPublishOnboarding) {
        setShowAfterPublish(true)
      } else {
        notification.showSuccess('Widget published successfully')
      }
      resetPublishStatus(widget.id!)
    }

    if (resp.status === 'queuing' || resp.status === 'publishing') {
      setState('publishQueuing')
      timerId.current = setTimeout(fetchStatus, 3000)
    }

    if (resp.status === 'failed') {
      setState(undefined)
      notification.showError('Widget publishing failed. Please try again')
      resetPublishStatus(widget.id!)
    }
  }, [profile.data?.doNotShowAfterPublishOnboarding, widget.id])

  useEffect(() => {
    fetchStatus()
    return () => {
      clearTimeout(timerId.current)
    }
  }, [widget.id])

  useEffect(() => {
    if (isNew) {
      setOpenWizard(true)
    }
  }, [isNew])

  const onSave = async () => {
    clearTimeout(timerId.current)
    setState('saveLoading')

    await withHandledErrors(
      notification,
      async () => {
        try {
          await save()

          // do now await here, because we don't want to wait for the response
          publishPreviewWidget(widget.id!)
        } finally {
          setState(undefined)
        }
      },
      'Saved successfully',
    )
  }

  const save = async () => {
    const response = await saveWidget(widget)
    setSavedWidget(response)
    setWidget(response)
  }

  const onPublish = async () => {
    setState('publishLoading')

    await withHandledErrors(
      notification,
      async () => {
        try {
          await save()
          await publishWidget(widget.id!)
          // do now await here, because we don't want to wait for the response
          publishPreviewWidget(widget.id!)
          fetchStatus()
        } catch (e) {
          setState(undefined)
        }
      },
      null,
    )
  }

  const onPublishPreview = async () => {
    setState('previewLoading')

    await withHandledErrors(
      notification,
      async () => {
        try {
          await save()
          // do now await here, because we don't want to wait for the response
          publishPreviewWidget(widget.id!)
          window.open(
            pathToUrl(routes.publicRoutes.widgetPreview, { id: widget.id }),
            '_blank',
          )
        } finally {
          setState(undefined)
        }
      },
      null,
    )
  }

  return (
    <>
      <HelpPopup open={openHelp} onClose={() => setOpenHelp(false)} />
      {openWizard && <AiWizard initialAction="onboarding" noTrigger />}
      {!isDemo && (
        <ShareCode open={shareCodeOpen} onClose={() => setShareCodeOpen(false)} />
      )}
      <Header>
        <Grid flow="column" gap={16}>
          {!isDemo ? (
            <>
              <IconButton
                icon={AiOutlineQuestionCircle}
                onClick={() => {
                  sendAnalyticsEvent(AnalyticsEvents.helpPopupOpened)
                  setOpenHelp(true)
                }}
                size="middle"
                color={Color.Grey_10}
              >
                <HeaderBtnContent>Need help?</HeaderBtnContent>
              </IconButton>
              <IconButton
                icon={BsCodeSlash}
                onClick={() => {
                  sendAnalyticsEvent(AnalyticsEvents.installPopupOpened)
                  setShareCodeOpen(true)
                }}
                size="middle"
                color={Color.Grey_10}
              >
                <HeaderBtnContent>Install to your website</HeaderBtnContent>
              </IconButton>
              <IconButton
                loading={state === 'previewLoading'}
                disabled={!!state}
                onClick={() => {
                  sendAnalyticsEvent(AnalyticsEvents.previewClicked)
                  onPublishPreview()
                }}
                size="middle"
                icon={AiOutlineEye}
                color={Color.Grey_10}
              >
                <HeaderBtnContent>Preview</HeaderBtnContent>
              </IconButton>
              <IconButton
                icon={changed ? undefined : AiOutlineCheck}
                loading={state === 'saveLoading'}
                disabled={!!state}
                onClick={() => {
                  sendAnalyticsEvent(AnalyticsEvents.saveClicked)
                  onSave()
                }}
                size="middle"
                type="default"
                iconSize={14}
              >
                {changed ? 'Save' : 'Saved'}
              </IconButton>
              <Tooltip
                open={isInABTest !== true ? false : undefined}
                placement="bottom"
                title="You cannot use this widget as is currently used in an A/B test. Please make a copy of this widget if you want to use it separately."
              >
                <IconButton
                  icon={AiOutlineCloudUpload}
                  loading={state === 'publishLoading' || state === 'publishQueuing'}
                  disabled={!!state || isInABTest === undefined || isInABTest === true}
                  onClick={() => {
                    sendAnalyticsEvent(AnalyticsEvents.publishClicked)
                    onPublish()
                  }}
                  type="primary"
                  size="middle"
                >
                  {state === 'publishQueuing' ? 'Queuing' : 'Publish'}
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <IconButton
                type="link"
                size="middle"
                onClick={() => {
                  localStorage.setItem('tempWidget', JSON.stringify(widget))
                  router.push(routes.guestRoutes.signup)
                }}
                color={Color.White}
              >
                Signup
              </IconButton>
              <IconButton
                icon={AiOutlineCloudUpload}
                type="primary"
                size="middle"
                onClick={() => {
                  localStorage.setItem('tempWidget', JSON.stringify(widget))
                  router.push(routes.guestRoutes.signup)
                }}
              >
                Save
              </IconButton>
            </>
          )}
        </Grid>
      </Header>
      <Wrap>
        <Flex flexDirection="row" height="100%">
          <BuilderSideMenu />
          {isAnalytics ? (
            <>{children}</>
          ) : (
            <>
              <MainContent>
                <PreviewBtn type="default" onClick={() => setShowPreview(true)}>
                  Show preview
                </PreviewBtn>
                {children}
              </MainContent>
              <WidgetPreview open={showPreview} onClose={() => setShowPreview(false)} />
            </>
          )}
        </Flex>
      </Wrap>
      <Footer />
      <AfterPublishPopup
        open={showAfterPublish}
        onClose={() => setShowAfterPublish(false)}
      />
      <MainOnboarding
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        demo={isDemo}
      />
    </>
  )
}

export default (props: Props) => <BuilderPage {...props} />
